import React, { useState, useRef, useCallback } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import convertIcon from '../assets/pdf-png.png';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PdfToPng = () => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [submitValue, setSubmitValue] = useState(constants.convert);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const [resolution, setResolution] = useState(constants.medium);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const cancelRef = useRef(false);
  const batchSize = 20;

  const toggleModal = () => {
    setVisible(!visible);
    if (!visible) {
      setFile(null);
      setErrorMessage('');
      setProgress(0);
      setIsProcessing(false);
      cancelRef.current = false;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Function to handle cancellation
  const handleCancellation = () => {
    cancelRef.current = true;
    setIsProcessing(false);
    setSubmitValue(constants.convert);
    setProgress(0);
    setErrorMessage('');
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === constants.pdfFileType) {
      setFile(selectedFile);
      setErrorMessage('');
    } else {
      setErrorMessage(constants.erroredFile);
    }
  };

  const convertPdfToPng = async () => {
    if (!file) {
      setErrorMessage(constants.pdfFile);
      return;
    }

    setSubmitValue(constants.processing);
    setIsProcessing(true);
    setProgress(0);
    cancelRef.current = false;
    try {
      const arrayBuffer = await file.arrayBuffer();

      // Create a zip file to store all images
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Use PDF.js for rendering
      const pdfJsDoc = await pdfjsLib.getDocument(arrayBuffer).promise;
      const totalPages = pdfJsDoc.numPages;
      // Set scale based on resolution
      let scale;
      switch (resolution) {
        case constants.low:
          scale = 1.0;
          break;
        case constants.medium:
          scale = 1.5;
          break;
        case constants.high:
          scale = 2.0;
          break;
        default:
          scale = 1.5;
      }

      // Process pages in batches
      for (let batchStart = 0; batchStart < totalPages; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, totalPages);
        const batchPromises = [];
        // Create promises for each page in the batch
        for (let i = batchStart; i < batchEnd; i++) {
          batchPromises.push(processPage(i, pdfJsDoc, scale, zip));
        }
        // Wait for all pages in this batch to complete
        await Promise.all(batchPromises);

        updateProgress(batchEnd, totalPages);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Generate and download zip file
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        async: true,
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      if (cancelRef.current) {
        setErrorMessage(constants.processingCancelled);
        throw new Error(constants.processingCancelled);
      }

      const downloadUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${file.name.replace('.pdf', '')}_images.zip`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(downloadUrl);

      setProgress(100);

      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);

      setSubmitValue(constants.convert);
      setIsProcessing(false);
      setVisible(false);
    } catch (error) {
      console.error(error);
      setSubmitValue(constants.convert);
      setIsProcessing(false);
      if (!cancelRef.current) {
        setErrorMessage(constants.conversionFailed);
      }
    }
  };

  const updateProgress = useCallback((current, total) => {
    const newProgress = Math.round((current / total) * 100);
    setProgress(newProgress);
  }, []);

  // Helper function to process a single page
  const processPage = async (pageIndex, pdfJsDoc, scale, zip) => {
    const pdfJsPage = await pdfJsDoc.getPage(pageIndex + 1);

    const pageSize = pdfJsPage._pageInfo.view;

    // Calculate scaled dimensions
    const scaledWidth = pageSize[2] * scale;
    const scaledHeight = pageSize[3] * scale;
    let canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = scaledHeight;
    canvas.width = scaledWidth;

    // Set white background
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Create viewport using dimensions from pdf-lib
    const viewport = pdfJsPage.getViewport({ scale });

    // Create a render task but don't await it immediately
    const renderTask = pdfJsPage.render({
      canvasContext: context,
      viewport,
      renderInteractiveForms: true,
      enableWebGL: true,
    });

    const renderPromise = new Promise((resolve, reject) => {
      // Check for cancellation periodically during rendering
      const checkCancellation = setInterval(() => {
        if (cancelRef.current) {
          clearInterval(checkCancellation);
          renderTask.cancel();
          reject(new Error('Processing cancelled'));
        }
      }, 100);

      // When rendering completes, clear the interval and resolve
      renderTask.promise
        .then(() => {
          clearInterval(checkCancellation);
          resolve();
        })
        .catch((error) => {
          clearInterval(checkCancellation);
          reject(error);
        });
    });

    // Wait for rendering to complete or be cancelled
    await renderPromise;

    // Convert canvas to PNG
    const imgData = canvas.toDataURL('image/png');
    const base64Data = imgData.replace(/^data:image\/(png|jpg);base64,/, '');

    // Add PNG to zip
    zip.file(`page-${pageIndex + 1}.png`, base64Data, { base64: true });

    canvas.width = 0;
    canvas.height = 0;
    canvas = null;
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        <div className="service-card-icon">
          <img src={convertIcon} alt="PDF to PNG" loading="lazy" />
        </div>
        <h3 className="service-card-title">{constants.pdfToPng}</h3>
        <p className="service-card-desc">{constants.PdfToPngText}</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={isProcessing ? handleCancellation : toggleModal}>
                {constants.cancel}
              </Button>
              <Button variant="primary" onClick={convertPdfToPng} disabled={isProcessing || file == null}>
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={constants.pdfToPngConverter}
      >
        <FormField label={constants.uploadFile} description={constants.onlyPdfAllowed} errorText={errorMessage}>
          <input
            ref={fileInputRef}
            id="file-input"
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
        </FormField>

        {isProcessing && (
          <div style={{ marginTop: '15px' }}>
            <FormField label="Conversion Progress">
              <ProgressBar value={progress} label={`${progress}%`} description={`Processing PDF pages...`} />
            </FormField>
          </div>
        )}

        <div style={{ marginTop: '15px' }}>
          <FormField label={constants.imageQuality} description={constants.outputResolution}>
            <div>
              <label>
                <input
                  type="radio"
                  value={constants.low}
                  checked={resolution === constants.low}
                  onChange={(e) => setResolution(e.target.value)}
                  disabled={isProcessing}
                />
                {constants.lowResolution}
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  value={constants.medium}
                  checked={resolution === constants.medium}
                  onChange={(e) => setResolution(e.target.value)}
                  disabled={isProcessing}
                />
                {constants.mediumResolution}
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  value={constants.high}
                  checked={resolution === constants.high}
                  onChange={(e) => setResolution(e.target.value)}
                  disabled={isProcessing}
                />
                {constants.highResolution}
              </label>
            </div>
          </FormField>
        </div>
      </Modal>
    </div>
  );
};

export default PdfToPng;
